/**
 * Life4Earth V2 — Project Funding Sources API Adapter
 *
 * Endpoint: /api/projects/{guid}/funding-sources
 *
 * Tracks where project funding comes from: grants, donations, in-kind,
 * club treasury, municipal contributions, etc. This is the "revenue side"
 * of the project budget; the BOM (PreflightItem cost fields) is the
 * "expense side."
 *
 * Epic: Budget & Cost Tracking
 */

import { apiFetch } from "./client";

// ── Source Types ────────────────────────────────────────────

export const FUNDING_SOURCE_TYPES = [
  "grant",
  "donation",
  "in_kind",
  "club_treasury",
  "municipal",
  "corporate",
  "self_funded",
  "crowdfunding",
  "other",
] as const;

export type FundingSourceType = (typeof FUNDING_SOURCE_TYPES)[number];

export const FUNDING_STATUSES = [
  "pending",
  "confirmed",
  "received",
  "declined",
  "withdrawn",
] as const;

export type FundingStatus = (typeof FUNDING_STATUSES)[number];

// ── API Response Types ─────────────────────────────────────

export interface FundingSourceResponse {
  id: string;
  projectId: string;
  sourceName: string;
  sourceType: string;
  amountPledged: number;
  amountReceived: number;
  currency: string;
  status: string;
  contactName: string | null;
  contactInfo: string | null;
  notes: string | null;
  referenceId: string | null;
  pledgedAt: string | null;
  receivedAt: string | null;
  expectedDate: string | null;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FundingSummaryResponse {
  totalPledged: number;
  totalReceived: number;
  sourceCount: number;
  confirmedCount: number;
  pendingCount: number;
  currency: string;
}

// ── Client-side Type ───────────────────────────────────────

export interface FundingSource {
  id: string;
  projectId: string;
  sourceName: string;
  sourceType: FundingSourceType;
  amountPledged: number;
  amountReceived: number;
  currency: string;
  status: FundingStatus;
  contactName: string | null;
  contactInfo: string | null;
  notes: string | null;
  referenceId: string | null;
  pledgedAt: string | null;
  receivedAt: string | null;
  expectedDate: string | null;
  sortOrder: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Request Types ──────────────────────────────────────────

export interface UpsertFundingSourceRequest {
  sourceName: string;
  sourceType: string;
  amountPledged: number;
  amountReceived: number;
  currency: string;
  status: string;
  contactName?: string | null;
  contactInfo?: string | null;
  notes?: string | null;
  referenceId?: string | null;
  pledgedAt?: string | null;
  receivedAt?: string | null;
  expectedDate?: string | null;
  sortOrder?: number;
}

// ── Shape Converter ────────────────────────────────────────

export function apiResponseToFundingSource(
  r: FundingSourceResponse
): FundingSource {
  return {
    id: r.id,
    projectId: r.projectId,
    sourceName: r.sourceName,
    sourceType: r.sourceType as FundingSourceType,
    amountPledged: r.amountPledged,
    amountReceived: r.amountReceived,
    currency: r.currency,
    status: r.status as FundingStatus,
    contactName: r.contactName,
    contactInfo: r.contactInfo,
    notes: r.notes,
    referenceId: r.referenceId,
    pledgedAt: r.pledgedAt,
    receivedAt: r.receivedAt,
    expectedDate: r.expectedDate,
    sortOrder: r.sortOrder,
    createdBy: r.createdBy,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

// ── API Functions ──────────────────────────────────────────

/**
 * List all funding sources for a project.
 */
export async function fetchFundingSources(
  projectGuid: string
): Promise<FundingSourceResponse[]> {
  return apiFetch<FundingSourceResponse[]>(
    `/api/projects/${projectGuid}/funding-sources`
  );
}

/**
 * Get rollup summary (total pledged, received, counts).
 */
export async function fetchFundingSummary(
  projectGuid: string
): Promise<FundingSummaryResponse> {
  return apiFetch<FundingSummaryResponse>(
    `/api/projects/${projectGuid}/funding-sources/summary`
  );
}

/**
 * Create a new funding source.
 */
export async function createFundingSource(
  projectGuid: string,
  req: UpsertFundingSourceRequest
): Promise<FundingSourceResponse> {
  return apiFetch<FundingSourceResponse>(
    `/api/projects/${projectGuid}/funding-sources`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/**
 * Update an existing funding source.
 */
export async function updateFundingSource(
  projectGuid: string,
  sourceId: string,
  req: UpsertFundingSourceRequest
): Promise<FundingSourceResponse> {
  return apiFetch<FundingSourceResponse>(
    `/api/projects/${projectGuid}/funding-sources/${sourceId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/**
 * Delete a funding source.
 */
export async function deleteFundingSource(
  projectGuid: string,
  sourceId: string
): Promise<void> {
  return apiFetch<void>(
    `/api/projects/${projectGuid}/funding-sources/${sourceId}`,
    { method: "DELETE" }
  );
}
